import { ModuleNames } from '../../src/constants/componentNames.constants'
import {
  extractTeaserData,
  getFeaturedResources,
  getResourceListOrdering,
  getTopThreeFilters,
  sortResourceList,
} from '../../src/createPages/resourceCenterListing/createResourceListingPages.utils'
import formatComponentTeaserData from '../component/externalResource'
import formatComponentMicrocopySetData from '../component/microcopySet'
import formatComponentTabOrderData from '../component/tabOrder'
import MICROCOPY_RESOURCE_NAMES from '../constants/microcopyResourceNames'
import OLD_MODULE_MAPPING from '../constants/modulesMapping'
import MODULE_MAPPING from '../constants/newModuleMapping'
import { allAnalystReportData } from '../page/analystReport'
import { allCaseStudyData } from '../page/caseStudy'
import { allResourceDemoData } from '../page/resourceDemo'
import formatPageResourceListingData from '../page/resourceListing'
import { allWebinarData } from '../page/webinar'
import { allWhitePaperData } from '../page/whitepaper'
import { getAll, pageLocale } from '../preview.utils'

export default async (slug: string, client: any) => {
  try {
    const { name, code } = pageLocale(slug)

    let urlParts = slug.split('/')
    let pageSlug = ''

    let pageNumber = 1

    // remove the page number from the url
    if (isNaN(Number(urlParts[urlParts.length - 1]))) pageSlug = slug
    else {
      pageSlug = urlParts.slice(0, urlParts.length - 1).join('/')
      pageNumber = Number(urlParts[urlParts.length - 1])
    }

    let pageEntry = await client.getEntries({
      content_type: MODULE_MAPPING.PAGE_RESOURCE_LISTING,
      locale: code,
      'fields.entryLocale': name,
      'fields.slug': pageSlug,
      include: 10,
    })

    if (pageEntry.total === 0) {
      return {}
    }

    // remove circular references
    pageEntry = JSON.parse(pageEntry.stringifySafe())

    let content: any = pageEntry?.items?.[0]
      ? formatPageResourceListingData(pageEntry.items[0])
      : {}

    let allTeasers: any = await getAll(client, MODULE_MAPPING.TEASER, {
      locale: code,
      'fields.entryLocale': name,
    })

    allTeasers = allTeasers.map((item: any) => formatComponentTeaserData(item))

    let allTabOrders: any = await getAll(client, MODULE_MAPPING.TAB_ORDER, {
      locale: code,
      'fields.entryLocale': name,
    })

    allTabOrders = allTabOrders.map((item: any) => formatComponentTabOrderData(item))

    let microcopySetResources: any = await client.getEntries({
      content_type: MODULE_MAPPING.MICROCOPY_SET,
      'fields.key': MICROCOPY_RESOURCE_NAMES[code],
      include: 10,
    })

    microcopySetResources = microcopySetResources?.items?.[0]
      ? formatComponentMicrocopySetData(microcopySetResources.items[0])
      : {}

    const listingTabs =
      allTabOrders.filter((item: any) => {
        for (let entry of item.pageEntries) {
          if (entry.id === content.id) return true
        }
        return false
      })?.[0] || []

    let resources: any = []

    if (
      slug.indexOf('resources/all') > -1 ||
      slug.match('^/?((de|es|ja|ko|pt|es|fr|cn)/)resources(/[0-9]+)?$')
    )
      resources = await getAllPageListings(client, allTeasers, code, name)
    else if (slug.indexOf('resources/automation-analyst-reports') > -1)
      resources = await getIndividualPageListings(
        client,
        allTeasers,
        code,
        name,
        MODULE_MAPPING.PAGE_ANALYST_REPORT,
        'Analyst Report'
      )
    else if (slug.indexOf('resources/automation-whitepapers') > -1)
      resources = await getIndividualPageListings(
        client,
        allTeasers,
        code,
        name,
        MODULE_MAPPING.PAGE_WHITEPAPER,
        'White paper'
      )
    else if (slug.indexOf('resources/automation-case-studies') > -1)
      resources = await getIndividualPageListings(
        client,
        allTeasers,
        code,
        name,
        MODULE_MAPPING.PAGE_RESOURCE,
        'Case Study'
      )
    else if (slug.indexOf('resources/automation-webinars') > -1)
      resources = await getIndividualPageListings(
        client,
        allTeasers,
        code,
        name,
        MODULE_MAPPING.PAGE_PLACEHOLDER,
        'Webinar'
      )
    else if (slug.indexOf('resources/automation-demo') > -1)
      resources = await getIndividualPageListings(
        client,
        allTeasers,
        code,
        name,
        MODULE_MAPPING.PAGE_RESOURCE_DEMO,
        'Video Demo'
      )
    else if (slug.indexOf('resources/automation-brochures') > -1)
      resources = await getIndividualPageListings(
        client,
        allTeasers,
        code,
        name,
        MODULE_MAPPING.PAGE_BROCHURE,
        'Brochure'
      )
    else if (slug.indexOf('events') > -1)
      resources = await getIndividualPageListings(
        client,
        allTeasers,
        code,
        name,
        MODULE_MAPPING.PAGE_EVENTS,
        'Events'
      )

    if (Math.ceil(resources.length / 12) < pageNumber) return {}

    return {
      contentType: MODULE_MAPPING.PAGE_RESOURCE_LISTING,
      content,
      pageData: {
        location: {
          pathname: `/${slug}`,
          origin: 'http://localhost:8000',
        },
        path: slug,
        pageContext: {
          pageLocale: code,
          pageNumber,
          listingPageData: content,
          resourceData: resources,
          listingTabs: listingTabs,
          topThreeFilters: getTopThreeFilters(resources, content?.filters),
          featuredResources: getFeaturedResources(resources),
          microcopySetResources: microcopySetResources,
        },
      },
    }
  } catch (err) {
    console.log(err)
    return {
      error: err,
    }
  }
}

export const getIndividualPageListings = async (
  client: any,
  teasers: any,
  code: string,
  name: string,
  pageType: string,
  category: string
) => {
  let pageTypeWithPageModel = [
    MODULE_MAPPING.PAGE_ANALYST_REPORT,
    MODULE_MAPPING.PAGE_WHITEPAPER,
    MODULE_MAPPING.PAGE_RESOURCE,
    MODULE_MAPPING.PAGE_RESOURCE_DEMO,
    MODULE_MAPPING.PAGE_PLACEHOLDER,
  ]

  let filteredEntries: any = []

  if (pageTypeWithPageModel.includes(pageType)) {
    // otherwise it's a component teaser
    let allPageTypeEntries: any = await getAll(client, pageType, {
      locale: code,
      'fields.entryLocale': name,
    })

    let func =
      pageType === MODULE_MAPPING.PAGE_RESOURCE_DEMO
        ? allResourceDemoData
        : pageType === MODULE_MAPPING.PAGE_ANALYST_REPORT
        ? allAnalystReportData
        : pageType === MODULE_MAPPING.PAGE_WHITEPAPER
        ? allWhitePaperData
        : pageType === MODULE_MAPPING.PAGE_RESOURCE
        ? allCaseStudyData
        : pageType === MODULE_MAPPING.PAGE_PLACEHOLDER
        ? allWebinarData
        : () => {}

    let formattedEntries = allPageTypeEntries.map((item: any) => func(item))

    filteredEntries = formattedEntries.filter((item: any) => !item.excludeFromResourceCenter)
  }

  if (filteredEntries.length) {
    filteredEntries = filteredEntries.map((item: any) =>
      extractTeaserData(item, OLD_MODULE_MAPPING[pageType], true)
    )
    filteredEntries = sortResourceList(filteredEntries, true)
  }

  let filteredTeasers = teasers.filter(
    (item: any) => item?.category?.name?.includes(category) && item.includeInResourceCenter
  )

  filteredTeasers = filteredTeasers.map((item: any) =>
    extractTeaserData(item, ModuleNames.TEASER, true)
  )

  filteredTeasers = sortResourceList(filteredTeasers, true)

  let allResources = [...filteredEntries, ...filteredTeasers]

  allResources = getResourceListOrdering(allResources)
  return allResources
}

export const getAllPageListings = async (client: any, teasers: any, code: string, name: string) => {
  let allListings = [
    getIndividualPageListings(
      client,
      teasers,
      code,
      name,
      MODULE_MAPPING.PAGE_ANALYST_REPORT,
      'Analyst Report'
    ),
    getIndividualPageListings(
      client,
      teasers,
      code,
      name,
      MODULE_MAPPING.PAGE_WHITEPAPER,
      'White paper'
    ),
    getIndividualPageListings(
      client,
      teasers,
      code,
      name,
      MODULE_MAPPING.PAGE_RESOURCE,
      'Case Study'
    ),
    getIndividualPageListings(
      client,
      teasers,
      code,
      name,
      MODULE_MAPPING.PAGE_RESOURCE_DEMO,
      'Video Demo'
    ),
    getIndividualPageListings(
      client,
      teasers,
      code,
      name,
      MODULE_MAPPING.PAGE_PLACEHOLDER,
      'Webinar'
    ),
    getIndividualPageListings(
      client,
      teasers,
      code,
      name,
      MODULE_MAPPING.PAGE_BROCHURE,
      'Brochure'
    ),
    getIndividualPageListings(client, teasers, code, name, MODULE_MAPPING.PAGE_EVENTS, 'Events'),
  ]

  let allData = await Promise.all(allListings)

  allData = [
    ...allData[0],
    ...allData[1],
    ...allData[2],
    ...allData[3],
    ...allData[4],
    ...allData[5],
    ...allData[6],
  ]

  // allData = sortResourceList(allData)
  allData = getResourceListOrdering(allData)

  return allData
}
